import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  IconButton,
  InputAdornment,
  Divider,
  Card,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { Visibility, VisibilityOff, Delete as DeleteIcon, Add as AddIcon, UploadFile as UploadFileIcon } from '@mui/icons-material';
import axios from 'axios';
import config from '../config';
import AgentRuleBasedCallDialog from './AgentRuleBasedCallDialog';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

/** Align with API/agent schema booleans for load and persist. */
function toAgentBool(value) {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return false;
}

const AgentForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    customAgentId: '',
    name: '',
    email: '',
    password: '',
    phone: '',
    to: [],
    cc: [],
    bcc: [],
    subject: '',
    body: '',
    transcriptFilters: [],
    mailSendFromTranscript: false,
    aiReporting: false,
    'tutorCare-data-enable': false,
    hideTranscriptAudio: false,
    hideBeforeDate: null
  });
  const [newTo, setNewTo] = useState('');
  const [newCc, setNewCc] = useState('');
  const [newBcc, setNewBcc] = useState('');
  const [newFilterContent, setNewFilterContent] = useState('');
  const [newFilterEmails, setNewFilterEmails] = useState('');
  const [filterErrors, setFilterErrors] = useState({});
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const user = JSON.parse(localStorage.getItem('user'));
  const isAdmin = user?.role === 'admin';

  // TutorCare Excel upload state
  const [tutorCareInfo, setTutorCareInfo] = useState(null);
  const [tutorCareFile, setTutorCareFile] = useState(null);
  const [tutorCareUploading, setTutorCareUploading] = useState(false);
  const [tutorCareMessage, setTutorCareMessage] = useState('');
  const [tutorCareError, setTutorCareError] = useState('');

  useEffect(() => {
    const fetchAgent = async () => {
      if (id) {
        try {
          setLoading(true);
          const response = await axios.get(`${config.apiBaseUrl}/api/agents/${id}`);
          // Ensure transcriptFilters and mailSendFromTranscript are properly initialized
          const agentData = {
            ...response.data,
            transcriptFilters: Array.isArray(response.data.transcriptFilters) ? response.data.transcriptFilters : [],
            mailSendFromTranscript: response.data.mailSendFromTranscript || false,
            aiReporting: toAgentBool(response.data.aiReporting),
            'tutorCare-data-enable': toAgentBool(response.data['tutorCare-data-enable']),
            hideTranscriptAudio: Boolean(response.data.hideTranscriptAudio),
            hideBeforeDate: response.data.hideBeforeDate ? new Date(response.data.hideBeforeDate) : null
          };
          setFormData(agentData);
        } catch (err) {
          setError(err.response?.data?.message || 'Failed to fetch agent');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchAgent();
  }, [id]);

  useEffect(() => {
    if (id && isAdmin) {
      fetchTutorCareInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isAdmin]);

  const fetchTutorCareInfo = async () => {
    try {
      const response = await axios.get(`${config.apiBaseUrl}/api/agents/${id}/tutorcare`);
      setTutorCareInfo(response.data);
    } catch (err) {
      // Non-fatal: agent may simply have no TutorCare data yet.
      console.error('Error fetching TutorCare info:', err);
    }
  };

  const handleTutorCareUpload = async () => {
    setTutorCareMessage('');
    setTutorCareError('');
    if (!tutorCareFile) {
      setTutorCareError('Please choose an Excel (.xlsx/.xls) or CSV file first.');
      return;
    }
    setTutorCareUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', tutorCareFile);
      const response = await axios.post(
        `${config.apiBaseUrl}/api/agents/${id}/tutorcare-upload`,
        uploadData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setTutorCareMessage(
        `Uploaded "${response.data.sourceFileName || tutorCareFile.name}" — ${response.data.rowCount} row(s) stored (previous data replaced).`
      );
      setTutorCareFile(null);
      await fetchTutorCareInfo();
    } catch (err) {
      console.error('Error uploading TutorCare file:', err);
      setTutorCareError(err.response?.data?.message || 'Failed to upload file');
    } finally {
      setTutorCareUploading(false);
    }
  };

  const handleTutorCareDelete = async () => {
    setTutorCareMessage('');
    setTutorCareError('');
    try {
      const response = await axios.delete(`${config.apiBaseUrl}/api/agents/${id}/tutorcare`);
      setTutorCareMessage(`Deleted ${response.data.deletedCount} stored row(s).`);
      await fetchTutorCareInfo();
    } catch (err) {
      console.error('Error deleting TutorCare data:', err);
      setTutorCareError(err.response?.data?.message || 'Failed to delete data');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const handleAddTo = () => {
    if (newTo && !formData.to.includes(newTo)) {
      setFormData(prev => ({
        ...prev,
        to: [...prev.to, newTo]
      }));
      setNewTo('');
    }
  };

  const handleRemoveTo = (email) => {
    setFormData(prev => ({
      ...prev,
      to: prev.to.filter(e => e !== email)
    }));
  };

  const handleAddCc = () => {
    if (newCc && !formData.cc.includes(newCc)) {
      setFormData(prev => ({
        ...prev,
        cc: [...prev.cc, newCc]
      }));
      setNewCc('');
    }
  };

  const handleRemoveCc = (email) => {
    setFormData(prev => ({
      ...prev,
      cc: prev.cc.filter(e => e !== email)
    }));
  };

  const handleAddBcc = () => {
    if (newBcc && !formData.bcc.includes(newBcc)) {
      setFormData(prev => ({
        ...prev,
        bcc: [...prev.bcc, newBcc]
      }));
      setNewBcc('');
    }
  };

  const handleRemoveBcc = (email) => {
    setFormData(prev => ({
      ...prev,
      bcc: prev.bcc.filter(e => e !== email)
    }));
  };

  const handleAddTranscriptFilter = () => {
    const content = newFilterContent.trim();
    const emails = newFilterEmails.trim();
    
    if (!content) {
      setFilterErrors(prev => ({
        ...prev,
        transcriptFilter: 'Transcript content is required'
      }));
      return;
    }
    
    if (!emails) {
      setFilterErrors(prev => ({
        ...prev,
        transcriptFilter: 'At least one email is required'
      }));
      return;
    }
    
    // Parse emails
    const emailList = emails.split(',').map(email => email.trim()).filter(email => email);
    const invalidEmails = emailList.filter(email => !validateEmail(email));
    
    if (invalidEmails.length > 0) {
      setFilterErrors(prev => ({
        ...prev,
        transcriptFilter: `Invalid email(s): ${invalidEmails.join(', ')}`
      }));
      return;
    }
    
    // Check for duplicate content
    const existingContent = formData.transcriptFilters.some(filter => 
      filter.content.toLowerCase() === content.toLowerCase()
    );
    
    if (existingContent) {
      setFilterErrors(prev => ({
        ...prev,
        transcriptFilter: 'This transcript content filter already exists'
      }));
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      transcriptFilters: [...prev.transcriptFilters, {
        content: content,
        emails: emailList
      }]
    }));
    
    setNewFilterContent('');
    setNewFilterEmails('');
    setFilterErrors(prev => ({
      ...prev,
      transcriptFilter: undefined
    }));
  };

  const handleRemoveTranscriptFilter = (index) => {
    setFormData(prev => ({
      ...prev,
      transcriptFilters: prev.transcriptFilters.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Format email arrays and transcript filters before sending
      const formattedData = {
        ...formData,
        to: formData.to.map(email => email.trim().toLowerCase()).filter(email => email),
        cc: formData.cc.map(email => email.trim().toLowerCase()).filter(email => email),
        bcc: formData.bcc.map(email => email.trim().toLowerCase()).filter(email => email),
        transcriptFilters: formData.transcriptFilters.map(filter => ({
          content: filter.content.trim(),
          emails: filter.emails.map(email => email.trim().toLowerCase()).filter(email => email)
        })),
        mailSendFromTranscript: Boolean(formData.mailSendFromTranscript),
        aiReporting: toAgentBool(formData.aiReporting),
        'tutorCare-data-enable': toAgentBool(formData['tutorCare-data-enable']),
        hideTranscriptAudio: formData.hideTranscriptAudio || false,
        hideBeforeDate: formData.hideTranscriptAudio && formData.hideBeforeDate 
          ? formData.hideBeforeDate.toISOString() 
          : null
      };

      await (id
        ? axios.put(`${config.apiBaseUrl}/api/agents/${id}`, formattedData)
        : axios.post(`${config.apiBaseUrl}/api/agents`, formattedData));

      setSuccess(id ? 'Agent updated successfully!' : 'Agent created successfully!');
      setTimeout(() => {
        navigate('/agents');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save agent');
    } finally {
      setLoading(false);
    }
  };

  if (loading && id) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            {id ? 'Edit Agent' : 'Create Agent'}
          </Typography>
          {id && formData.customAgentId && !loading && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setRuleDialogOpen(true)}
            >
              Rule Based Definition
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Agent ID"
                name="customAgentId"
                value={formData.customAgentId}
                onChange={handleChange}
                disabled={!isAdmin}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                disabled={!isAdmin}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                disabled={!isAdmin}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                disabled={!isAdmin}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                disabled={!isAdmin}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                disabled={!isAdmin}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                multiline
                rows={4}
                label="Email Body"
                name="body"
                value={formData.body}
                onChange={handleChange}
                disabled={!isAdmin}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input
                  type="checkbox"
                  id="mailSendFromTranscript"
                  name="mailSendFromTranscript"
                  checked={Boolean(formData.mailSendFromTranscript)}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    mailSendFromTranscript: e.target.checked
                  }))}
                  disabled={!isAdmin}
                  style={{ width: '18px', height: '18px' }}
                />
                <label htmlFor="mailSendFromTranscript" style={{ cursor: isAdmin ? 'pointer' : 'default' }}>
                  <Typography variant="body1">
                    Mail-send-From-Transcript
                  </Typography>
                </label>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                When enabled, emails will be sent using agent-specific SMTP settings with the transcript content as the email body.
              </Typography>
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={toAgentBool(formData.aiReporting)}
                      onChange={(e) => {
                        const aiReporting = e.target.checked;
                        setFormData((prev) => ({ ...prev, aiReporting }));
                      }}
                      disabled={!isAdmin}
                    />
                  }
                  label={
                    <Box sx={{ pt: '2px' }}>
                      <Typography variant="body1" component="span">
                        AI reporting
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 0.5 }}
                      >
                        Current value: <strong>{toAgentBool(formData.aiReporting) ? 'true' : 'false'}</strong>{' '}
                        (persisted after Create / Update)
                      </Typography>
                    </Box>
                  }
                  sx={{ ml: -1, alignItems: 'flex-start' }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={toAgentBool(formData['tutorCare-data-enable'])}
                      onChange={(e) => {
                        const tutorCareEnabled = e.target.checked;
                        setFormData((prev) => ({ ...prev, 'tutorCare-data-enable': tutorCareEnabled }));
                      }}
                      disabled={!isAdmin}
                    />
                  }
                  label={
                    <Box sx={{ pt: '2px' }}>
                      <Typography variant="body1" component="span">
                        TutorCare Data enable
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 0.5 }}
                      >
                        Current value: <strong>{toAgentBool(formData['tutorCare-data-enable']) ? 'true' : 'false'}</strong>{' '}
                        (persisted after Create / Update)
                      </Typography>
                    </Box>
                  }
                  sx={{ ml: -1, alignItems: 'flex-start' }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(formData.hideTranscriptAudio)}
                        onChange={(e) => setFormData(prev => ({ ...prev, hideTranscriptAudio: e.target.checked }))}
                        disabled={!isAdmin}
                      />
                    }
                    label="Hide Transcript & Audio Before Date"
                  />
                  {formData.hideTranscriptAudio && (
                    <DatePicker
                      label="Hide Before Date"
                      value={formData.hideBeforeDate}
                      onChange={(newValue) => setFormData(prev => ({ ...prev, hideBeforeDate: newValue }))}
                      maxDate={(() => {
                        const yesterday = new Date();
                        yesterday.setDate(yesterday.getDate() - 1);
                        yesterday.setHours(23, 59, 59, 999);
                        return yesterday;
                      })()}
                      shouldDisableDate={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date >= today;
                      }}
                      disabled={!isAdmin}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          disabled: !isAdmin,
                          helperText: 'Select a past date (up to yesterday). Calls before this date will hide transcript and audio.'
                        }
                      }}
                    />
                  )}
                </Box>
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  To Email Addresses
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    fullWidth
                    label="Add To Email"
                    value={newTo}
                    onChange={(e) => setNewTo(e.target.value)}
                    disabled={!isAdmin}
                  />
                  {isAdmin && <Button onClick={handleAddTo}>Add</Button>}
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {formData.to.map((email) => (
                    <Chip
                      key={email}
                      label={email}
                      onDelete={isAdmin ? () => handleRemoveTo(email) : undefined}
                    />
                  ))}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  CC Email Addresses
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    fullWidth
                    label="Add CC Email"
                    value={newCc}
                    onChange={(e) => setNewCc(e.target.value)}
                    disabled={!isAdmin}
                  />
                  {isAdmin && <Button onClick={handleAddCc}>Add</Button>}
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {formData.cc.map((email) => (
                    <Chip
                      key={email}
                      label={email}
                      onDelete={isAdmin ? () => handleRemoveCc(email) : undefined}
                    />
                  ))}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  BCC Email Addresses
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    fullWidth
                    label="Add BCC Email"
                    value={newBcc}
                    onChange={(e) => setNewBcc(e.target.value)}
                    disabled={!isAdmin}
                  />
                  {isAdmin && <Button onClick={handleAddBcc}>Add</Button>}
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {formData.bcc.map((email) => (
                    <Chip
                      key={email}
                      label={email}
                      onDelete={isAdmin ? () => handleRemoveBcc(email) : undefined}
                    />
                  ))}
                </Box>
              </Box>
            </Grid>

            {/* Transcript Filtering  */}
            {isAdmin && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }}>
                  <Typography variant="h6" color="primary">
                    Transcript Filtering
                  </Typography>
                </Divider>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  When transcript content matches any filter, additional emails will be sent to the specified addresses.
                </Typography>
                
                {/* Add New Filter */}
                <Card sx={{ mb: 2, p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Add New Transcript Filter
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Transcript Content to Match"
                        value={newFilterContent}
                        onChange={(e) => setNewFilterContent(e.target.value)}
                        placeholder="Enter text to search in transcript"
                        helperText="Case-insensitive search"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Email Addresses"
                        value={newFilterEmails}
                        onChange={(e) => setNewFilterEmails(e.target.value)}
                        placeholder="email1@example.com, email2@example.com"
                        helperText="Separate multiple emails with commas"
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddTranscriptFilter}
                        sx={{ height: '40px' }}
                      >
                        Add Filter
                      </Button>
                    </Grid>
                  </Grid>
                  {filterErrors.transcriptFilter && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {filterErrors.transcriptFilter}
                    </Alert>
                  )}
                </Card>

                {/* Existing Filters */}
                {formData.transcriptFilters.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Existing Filters ({formData.transcriptFilters.length})
                    </Typography>
                    {formData.transcriptFilters.map((filter, index) => (
                      <Card key={index} sx={{ mb: 1, p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Content to match:
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                              "{filter.content}"
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Send to:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {filter.emails.map((email, emailIndex) => (
                                <Chip
                                  key={emailIndex}
                                  label={email}
                                  size="small"
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          </Box>
                          <IconButton
                            color="error"
                            onClick={() => handleRemoveTranscriptFilter(index)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Card>
                    ))}
                  </Box>
                )}
              </Grid>
            )}
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              type="submit"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : (id ? 'Update' : 'Create')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/agents')}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* TutorCare Excel Upload (existing agents, admin only) */}
      {id && isAdmin && (
        <Paper sx={{ p: 4, mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <UploadFileIcon color="primary" />
            <Typography variant="h6" component="h2">
              TutorCare Data (Excel Upload)
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload an Excel (.xlsx/.xls) or CSV file. The first row is treated as the headers (keys)
            and each subsequent row is stored as a record. Uploading a new file replaces all previously
            stored TutorCare data for this agent.
          </Typography>

          {tutorCareInfo && tutorCareInfo.rowCount > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Currently stored: <strong>{tutorCareInfo.rowCount}</strong> row(s)
              {tutorCareInfo.sourceFileName ? ` from "${tutorCareInfo.sourceFileName}"` : ''}.
              {tutorCareInfo.headers && tutorCareInfo.headers.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {tutorCareInfo.headers.map((h, i) => (
                    <Chip key={i} label={h} size="small" />
                  ))}
                </Box>
              )}
            </Alert>
          )}

          {tutorCareMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>{tutorCareMessage}</Alert>
          )}
          {tutorCareError && (
            <Alert severity="error" sx={{ mb: 2 }}>{tutorCareError}</Alert>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
              {tutorCareFile ? 'Change File' : 'Choose File'}
              <input
                hidden
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => {
                  setTutorCareFile(e.target.files && e.target.files[0] ? e.target.files[0] : null);
                  setTutorCareMessage('');
                  setTutorCareError('');
                }}
              />
            </Button>
            {tutorCareFile && (
              <Typography variant="body2" color="text.secondary">
                {tutorCareFile.name}
              </Typography>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={handleTutorCareUpload}
              disabled={!tutorCareFile || tutorCareUploading}
              startIcon={tutorCareUploading ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {tutorCareUploading ? 'Uploading...' : 'Upload & Replace'}
            </Button>
            {tutorCareInfo && tutorCareInfo.rowCount > 0 && (
              <>
                <Divider orientation="vertical" flexItem />
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleTutorCareDelete}
                  disabled={tutorCareUploading}
                >
                  Delete All
                </Button>
              </>
            )}
          </Box>
        </Paper>
      )}

      {/* Rule Based Definition Dialog */}
      {id && formData.customAgentId && (
        <AgentRuleBasedCallDialog
          open={ruleDialogOpen}
          onClose={() => setRuleDialogOpen(false)}
          agentCustomId={formData.customAgentId}
        />
      )}
    </Container>
  );
}

export default AgentForm; 